// src/store/modules/numbers.js
import NumbersService from '@/services/NumbersService'

const state = () => ({
  rows: [],
  loadingList: false,
  loadingOne: false,
  saving: false,
  deleting: false,
  error: null,
  current: null
})

const getters = {
  rows: (s) => s.rows,
  loadingList: (s) => s.loadingList,
  loadingOne: (s) => s.loadingOne,
  saving: (s) => s.saving,
  deleting: (s) => s.deleting,
  error: (s) => s.error,
  current: (s) => s.current
}

function unwrapData(res) {
  const payload = res?.data ?? res
  if (!payload) return null

  // index() returns { data: [...] }
  if (Array.isArray(payload.data)) return payload.data

  // show() returns { data: {...} }
  if (payload.data && typeof payload.data === 'object') return payload.data

  // fallback
  return payload
}

const mutations = {
  SET_LOADING_LIST(s, v) { s.loadingList = v },
  SET_LOADING_ONE(s, v) { s.loadingOne = v },
  SET_SAVING(s, v) { s.saving = v },
  SET_DELETING(s, v) { s.deleting = v },
  SET_ERROR(s, v) { s.error = v },

  SET_LIST(s, rows) {
    s.rows = Array.isArray(rows) ? rows : []
  },

  SET_CURRENT(s, row) {
    s.current = row || null
  },

  UPSERT_IN_LIST(s, row) {
    const id = row?.Number
    if (!id) return
    const idx = s.rows.findIndex(r => r?.Number === id)
    if (idx === -1) s.rows = [row, ...s.rows]
    else s.rows.splice(idx, 1, row)
  },

  REMOVE_FROM_LIST(s, id) {
    s.rows = s.rows.filter(r => r?.Number !== id)
  }
}

const actions = {
  async fetchList({ commit }, params = {}) {
    commit('SET_LOADING_LIST', true)
    commit('SET_ERROR', null)
    try {
      const res = await NumbersService.list(params)
      const rows = unwrapData(res)
      commit('SET_LIST', rows)
      return rows
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to load numbers')
      throw e
    } finally {
      commit('SET_LOADING_LIST', false)
    }
  },

  async fetchOne({ commit }, { id, params } = {}) {
    if (!id) throw new Error('Missing id')
    commit('SET_LOADING_ONE', true)
    commit('SET_ERROR', null)
    try {
      const res = await NumbersService.get(id, params || {})
      const row = unwrapData(res)
      commit('SET_CURRENT', row)
      return row
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to load number')
      throw e
    } finally {
      commit('SET_LOADING_ONE', false)
    }
  },

  async create({ commit }, payload) {
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      const res = await NumbersService.create(payload || {})
      const created = unwrapData(res) || (res?.data ?? res)
      // controller returns { message, data }
      const row = created?.data && typeof created.data === 'object' ? created.data : created
      commit('UPSERT_IN_LIST', row)
      commit('SET_CURRENT', row)
      return row
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to create number')
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async update({ commit }, { id, payload } = {}) {
    if (!id) throw new Error('Missing id')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      const res = await NumbersService.update(id, payload || {})
      const updated = unwrapData(res) || (res?.data ?? res)
      // controller returns { message, data }
      const row = updated?.data && typeof updated.data === 'object' ? updated.data : updated
      commit('UPSERT_IN_LIST', row)
      commit('SET_CURRENT', row)
      return row
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to update number')
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async patch({ commit }, { id, payload } = {}) {
    if (!id) throw new Error('Missing id')
    commit('SET_SAVING', true)
    commit('SET_ERROR', null)
    try {
      const res = await NumbersService.patch(id, payload || {})
      const updated = unwrapData(res) || (res?.data ?? res)
      const row = updated?.data && typeof updated.data === 'object' ? updated.data : updated
      commit('UPSERT_IN_LIST', row)
      commit('SET_CURRENT', row)
      return row
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to update number')
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async remove({ commit }, id) {
    if (!id) throw new Error('Missing id')
    commit('SET_DELETING', true)
    commit('SET_ERROR', null)
    try {
      await NumbersService.destroy(id)
      commit('REMOVE_FROM_LIST', id)
      return true
    } catch (e) {
      commit('SET_ERROR', e?.response?.data || e?.message || 'Failed to delete number')
      throw e
    } finally {
      commit('SET_DELETING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
